/**
 * React Query hooks for user management
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { usersApi, type UserCreateData, type UserUpdateData } from '../services/api/users';
import type { User, UserRole } from '../types/user';
import { useEffectiveCompanyId } from './useEffectiveCompany';
import { useToast } from './useToast';

/**
 * Hook to list users
 */
export const useUsers = (options?: { role?: UserRole; activeOnly?: boolean }) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['users', companyId, options],
    () => usersApi.list(companyId, options),
    {
      enabled: !!companyId,
    }
  );
};

/**
 * Hook to get a single user
 */
export const useUser = (userId: string) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['users', userId, companyId],
    () => usersApi.getById(userId, companyId),
    {
      enabled: !!userId && !!companyId,
    }
  );
};

/**
 * Hook to get user limit status
 */
export const useUserLimitStatus = () => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['users', 'limit', companyId],
    () => usersApi.getLimitStatus(companyId),
    {
      enabled: !!companyId,
    }
  );
};

/**
 * Hook to create a user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (data: UserCreateData) => usersApi.create(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', companyId]);
        queryClient.invalidateQueries(['users', 'limit', companyId]);
        showSuccess('Usuario creado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al crear usuario');
      },
    }
  );
};

/**
 * Hook to update a user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    ({ userId, data }: { userId: string; data: UserUpdateData }) =>
      usersApi.update(userId, companyId, data),
    {
      onSuccess: (updatedUser: User) => {
        queryClient.invalidateQueries(['users', companyId]);
        queryClient.setQueryData(['users', updatedUser.userId, companyId], updatedUser);
        showSuccess('Usuario actualizado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al actualizar usuario');
      },
    }
  );
};

/**
 * Hook to deactivate a user
 */
export const useDeactivateUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (userId: string) => usersApi.deactivate(userId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', companyId]);
        queryClient.invalidateQueries(['users', 'limit', companyId]);
        showSuccess('Usuario desactivado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al desactivar usuario');
      },
    }
  );
};

/**
 * Hook to activate a user
 */
export const useActivateUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (userId: string) => usersApi.activate(userId, companyId),
    {
      onSuccess: (activatedUser: User) => {
        queryClient.invalidateQueries(['users', companyId]);
        queryClient.setQueryData(['users', activatedUser.userId, companyId], activatedUser);
        queryClient.invalidateQueries(['users', 'limit', companyId]);
        showSuccess('Usuario activado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al activar usuario');
      },
    }
  );
};
